import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import client from '../Contentful_API/api';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHero, LoadingState } from '../Common/PageLayout';
import { tokens } from '../../theme';

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(7, 0, 9),
    backgroundColor: tokens.surface,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(5, 0, 6) },
  },
  intro: { maxWidth: 720, marginBottom: theme.spacing(5) },
  overline: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: tokens.blue600,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  introTitle: { marginBottom: theme.spacing(1.5) },
  introText: { margin: 0, color: tokens.inkSoft, fontSize: '1.0625rem', lineHeight: 1.7 },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 218,
    padding: theme.spacing(3.5),
    border: `1px solid ${tokens.border}`,
    borderTop: `4px solid ${tokens.blue600}`,
    borderRadius: tokens.radius,
    backgroundColor: tokens.white,
    color: tokens.ink,
    textDecoration: 'none',
    boxShadow: tokens.shadowSm,
    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
    '&:hover': {
      borderTopColor: tokens.gold,
      boxShadow: tokens.shadowMd,
      transform: 'translateY(-3px)',
    },
    '&:focus-visible': { outline: `3px solid ${tokens.gold}`, outlineOffset: 3 },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
    marginBottom: theme.spacing(2.5),
    borderRadius: 6,
    backgroundColor: tokens.blue50,
    color: tokens.blue600,
  },
  cardTitle: { marginBottom: theme.spacing(1), color: tokens.ink },
  cardText: { flexGrow: 1, margin: 0, color: tokens.muted, fontSize: '0.9375rem', lineHeight: 1.6 },
  action: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: theme.spacing(2.5),
    color: tokens.blue600,
    fontSize: '0.875rem',
    fontWeight: 700,
  },
}));

function AidCard({ menu }) {
  const classes = useStyles();
  const title = menu.fields?.title || 'Aid resource';

  return (
    <Link className={classes.card} to={`/pagedetails/${menu.fields?.slug}`}>
      <span className={classes.icon} aria-hidden="true"><AccountBalanceOutlinedIcon /></span>
      <Typography variant="h5" component="h2" className={classes.cardTitle}>{title}</Typography>
      <p className={classes.cardText}>Explore official information, reports and resources related to development assistance.</p>
      <span className={classes.action}>Open resource <ArrowForwardIcon fontSize="small" /></span>
    </Link>
  );
}

export function ResourcePage({ resourceType = 'dpmu', pageTitle, pageDescription }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [menus, setMenus] = useState(null);

  useEffect(() => {
    let active = true;
    client.getEntries({ content_type: 'menus', 'fields.type': resourceType, order: 'fields.menuorder' })
      .then((response) => { if (active) setMenus(response.items || []); })
      .catch(() => { if (active) setMenus([]); });
    return () => { active = false; };
  }, [resourceType]);

  if (menus === null) return <LoadingState label="Loading aid information" />;

  return (
    <>
      <PageHero
        eyebrow={resourceType === 'economy' ? t('Economy & aid') : t('Aid and external resources')}
        title={pageTitle}
        description={pageDescription}
        breadcrumbs={[{ label: pageTitle }]}
      />
      <main className={classes.section}>
        <Container maxWidth="lg">
          <div className={classes.intro}>
            <span className={classes.overline}>Find what you need</span>
            <Typography variant="h2" component="h2" className={classes.introTitle}>Aid resources</Typography>
            <p className={classes.introText}>Browse official reports, policy material and other resources published by the Ministry of Finance.</p>
          </div>
          {menus.length > 0 ? (
            <Grid container spacing={3}>
              {menus.map((menu) => <Grid item xs={12} sm={6} md={4} key={menu.sys?.id || menu.fields?.slug}><AidCard menu={menu} /></Grid>)}
            </Grid>
          ) : <Typography color="textSecondary">No aid resources are available at the moment.</Typography>}
        </Container>
      </main>
    </>
  );
}

export default function AidPage() {
  const { t } = useTranslation();
  return (
    <ResourcePage
      pageTitle={t('Aid and external resources')}
      pageDescription="Official publications and information about development assistance and international economic cooperation."
    />
  );
}
